import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lightbulb, Plus, Trash2, Edit, X, Check, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BauDeInspiracoes() {
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'ideia'
  });

  const categories = [
    { value: 'ideia', label: 'Ideia', color: 'bg-amber-500/10 text-amber-600' },
    { value: 'personagem', label: 'Personagem', color: 'bg-blue-500/10 text-blue-600' },
    { value: 'cena', label: 'Cena', color: 'bg-green-500/10 text-green-600' },
    { value: 'dialogo', label: 'Diálogo', color: 'bg-purple-500/10 text-purple-600' },
    { value: 'pesquisa', label: 'Pesquisa', color: 'bg-red-500/10 text-red-600' }
  ];

  useEffect(() => {
    fetchInspirations();
  }, []);

  const fetchInspirations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inspirations`, {
        withCredentials: true
      });
      setInspirations(response.data);
    } catch (error) {
      console.error('Error fetching inspirations:', error);
      toast.error('Erro ao carregar inspirações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    try {
      if (editingId) {
        await axios.put(
          `${API_URL}/api/inspirations/${editingId}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Inspiração atualizada!');
      } else {
        await axios.post(
          `${API_URL}/api/inspirations`,
          formData,
          { withCredentials: true }
        );
        toast.success('Inspiração guardada no baú!');
      }
      
      setFormData({ title: '', content: '', category: 'ideia' });
      setShowForm(false);
      setEditingId(null);
      fetchInspirations();
    } catch (error) {
      console.error('Error saving inspiration:', error);
      toast.error('Erro ao salvar inspiração');
    }
  };

  const handleEdit = (inspiration) => {
    setFormData({
      title: inspiration.title,
      content: inspiration.content || '',
      category: inspiration.category || 'ideia'
    });
    setEditingId(inspiration.inspiration_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta inspiração?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/inspirations/${id}`, {
        withCredentials: true
      });
      toast.success('Inspiração removida do baú');
      fetchInspirations();
    } catch (error) {
      console.error('Error deleting inspiration:', error);
      toast.error('Erro ao excluir inspiração');
    }
  };

  const getCategoryBadge = (category) => {
    const cat = categories.find(c => c.value === category) || categories[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${cat.color}`}>
        {cat.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="bau-inspiracoes-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-light mb-2">Baú de Inspirações</h1>
              <p className="text-muted-foreground">
                {inspirations.length} {inspirations.length === 1 ? 'inspiração guardada' : 'inspirações guardadas'}
              </p>
            </div>
            <Button
              onClick={() => {
                setFormData({ title: '', content: '', category: 'ideia' });
                setEditingId(null);
                setShowForm(true);
              }}
              className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              data-testid="add-inspiration-button"
            >
              <Plus className="mr-2" size={20} />
              Nova Inspiração
            </Button>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-serif">
                    {editingId ? 'Editar Inspiração' : 'Nova Inspiração'}
                  </h2>
                  <button 
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Título *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Uma ideia brilhante..."
                      data-testid="inspiration-title-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Categoria</label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, category: cat.value })}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            formData.category === cat.value
                              ? cat.color + ' ring-2 ring-offset-2 ring-amber-500'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Descrição</label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Descreva sua inspiração..."
                      rows={4}
                      data-testid="inspiration-content-input"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      className="bg-amber-500 hover:bg-amber-600 text-stone-900 flex-1"
                      data-testid="save-inspiration-button"
                    >
                      <Check className="mr-2" size={18} />
                      {editingId ? 'Salvar Alterações' : 'Guardar no Baú'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingId(null);
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Inspirations List */}
          {inspirations.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-lg">
              <Lightbulb className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-medium mb-2">O baú está vazio</h3>
              <p className="text-muted-foreground mb-6">
                Guarde suas ideias, personagens, cenas e pesquisas aqui
              </p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              >
                Guardar Primeira Inspiração
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {inspirations.map((inspiration) => (
                <div
                  key={inspiration.inspiration_id}
                  className="bg-card border border-border/40 rounded-lg p-5 hover:shadow-md transition-all"
                  data-testid={`inspiration-card-${inspiration.inspiration_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium mb-2">{inspiration.title}</h3>
                      {getCategoryBadge(inspiration.category)}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(inspiration)}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                        data-testid={`edit-inspiration-${inspiration.inspiration_id}`}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(inspiration.inspiration_id)}
                        className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                        data-testid={`delete-inspiration-${inspiration.inspiration_id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  {inspiration.content && (
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {inspiration.content}
                    </p>
                  )}
                  
                  <p className="text-xs text-muted-foreground mt-3">
                    {new Date(inspiration.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
